/**
 * Pure helpers shared by the field components. No React, no bindx — so the
 * rules (URL prefix per locale, redirect path, external URL) are unit-tested
 * without a store.
 */

/** `'' | '/es' | '/cs'` → the addon shown in front of the slug input (`/`, `/es/`, `/cs/`). */
export function slugPrefixLabel(pathPrefix: string): string {
	return pathPrefix === '' ? '/' : `${pathPrefix}/`
}

/**
 * Decision 29: a `Url.url` must equal `pathPrefix || '/'` or start with
 * `pathPrefix + '/'`. Same rule as the import.
 */
export function isUrlValidForPrefix(url: string, pathPrefix: string): boolean {
	if (url === (pathPrefix || '/')) return true
	return url.startsWith(`${pathPrefix}/`) && url.length > pathPrefix.length + 1
}

/** Full `Url.url` from a locale prefix and the editable slug part (`''` → the locale home). */
export function joinSlug(pathPrefix: string, slug: string): string {
	const cleaned = slug.replace(/^\/+/, '').replace(/\/+$/, '')
	if (cleaned === '') return pathPrefix || '/'
	return `${pathPrefix}/${cleaned}`
}

/** The editable slug part of a full `Url.url` for a locale prefix (`'/es/precios'` → `'precios'`). */
export function splitSlug(url: string | null, pathPrefix: string): string {
	if (url == null) return ''
	if (url === (pathPrefix || '/')) return ''
	const head = `${pathPrefix}/`
	return url.startsWith(head) ? url.slice(head.length) : url.replace(/^\/+/, '')
}

export function slugify(value: string): string {
	return value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9/]+/g, '-')
		.replace(/\/+/g, '/')
		.replace(/^-+|-+$/g, '')
		.replace(/-\//g, '/')
		.replace(/\/-/g, '/')
}

/** Redirect `fromPath` / `toPath` and any site-relative path: must start with `/`, no whitespace. */
export function isSitePath(value: string): boolean {
	return value.startsWith('/') && !/\s/.test(value)
}

/** External link target: absolute http(s) URL, `mailto:` or `tel:`. */
export function isExternalHref(value: string): boolean {
	const trimmed = value.trim()
	if (/^(mailto:|tel:)/i.test(trimmed)) return trimmed.length > 7
	if (!/^https?:\/\//i.test(trimmed)) return false
	try {
		new URL(trimmed)
		return true
	} catch {
		return false
	}
}

export function isEmptyText(value: string | null | undefined): boolean {
	return value == null || value.trim() === ''
}

/** Anchor is stored without the leading `#`. */
export function normalizeAnchor(value: string | null): string | null {
	if (value == null) return null
	const trimmed = value.trim().replace(/^#+/, '')
	return trimmed === '' ? null : trimmed
}

export type PublishStatus = 'draft' | 'scheduled' | 'published'

export function publishStatus(publishedAt: string | null, now: Date = new Date()): PublishStatus {
	if (publishedAt == null || publishedAt === '') return 'draft'
	const at = new Date(publishedAt)
	if (Number.isNaN(at.getTime())) return 'draft'
	return at.getTime() <= now.getTime() ? 'published' : 'scheduled'
}
